import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Col,
  Collapse,
  DatePicker,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Timeline,
  message
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { NAMESPACE } from '@/constants';
import { useEditBookingMutation, useLazyGetBookingHistoryQuery } from '@/api';
import type { BookingRow, BookingGeneration, BookingFieldChange } from '@/models';

type FieldKind = 'text' | 'number' | 'date';

interface FieldSpec {
  name: keyof BookingRow;
  labelKey: string;
  kind: FieldKind;
}

/**
 * Fields an amend may change, grouped as the dialog shows them.
 *
 * Mirrors EDITABLE_FIELDS on the API, which refuses anything else: every
 * business column, but never an id or an audit column.
 */
const SECTIONS: { titleKey: string; fields: FieldSpec[] }[] = [
  {
    titleKey: 'editSectionOperational',
    fields: [
      { name: 'po', labelKey: 'po', kind: 'text' },
      { name: 'am', labelKey: 'am', kind: 'text' },
      { name: 'trader', labelKey: 'trader', kind: 'text' },
      { name: 'buying_point', labelKey: 'buyingPoint', kind: 'text' },
      { name: 'data_provider', labelKey: 'dataProvider', kind: 'text' },
      { name: 'audience_id', labelKey: 'audienceId', kind: 'text' },
      { name: 'audience_strategy_id', labelKey: 'audienceStrategyId', kind: 'text' },
      { name: 'targeting_kpi', labelKey: 'targetingKpi', kind: 'text' },
      { name: 'targeting_details', labelKey: 'targetingDetails', kind: 'text' }
    ]
  },
  {
    titleKey: 'editSectionFlight',
    fields: [
      { name: 'start_date', labelKey: 'startDate', kind: 'date' },
      { name: 'end_date', labelKey: 'endDate', kind: 'date' },
      { name: 'channel', labelKey: 'channel', kind: 'text' },
      { name: 'platform', labelKey: 'platform', kind: 'text' },
      { name: 'product', labelKey: 'product', kind: 'text' },
      { name: 'format', labelKey: 'format', kind: 'text' },
      { name: 'buy_model', labelKey: 'buyModel', kind: 'text' },
      { name: 'country', labelKey: 'country', kind: 'text' }
    ]
  },
  {
    titleKey: 'editSectionCommercial',
    fields: [
      { name: 'currency', labelKey: 'currency', kind: 'text' },
      { name: 'quantity', labelKey: 'quantity', kind: 'number' },
      { name: 'rate', labelKey: 'rate', kind: 'number' },
      { name: 'gross', labelKey: 'gross', kind: 'number' },
      { name: 'discount', labelKey: 'discount', kind: 'number' },
      { name: 'net', labelKey: 'net', kind: 'number' },
      { name: 'media_spend', labelKey: 'mediaSpend', kind: 'number' },
      { name: 'fee', labelKey: 'fee', kind: 'number' }
    ]
  }
];

const FIELDS = SECTIONS.flatMap((section) => section.fields);
const FIELD_BY_NAME = new Map(FIELDS.map((field) => [field.name as string, field]));

interface Props {
  /** The line item being amended, or null when the dialog is closed. */
  booking: BookingRow | null;
  onClose: () => void;
}

type FormValue = string | number | Dayjs | null | undefined;
type Values = Record<string, FormValue> & { reason?: string };
type Payload = string | number | null;

/** A row's value as the form control for its kind expects it. */
const toFormValue = (kind: FieldKind, value: unknown): FormValue => {
  if (value === null || value === undefined || value === '') return kind === 'text' ? '' : null;
  if (kind === 'date') return dayjs(String(value));
  if (kind === 'number') return Number(value);
  return String(value);
};

/** A form value as the API takes it — and as it is compared with the row. */
const toPayload = (kind: FieldKind, value: FormValue): Payload => {
  if (value === null || value === undefined || value === '') return null;
  if (kind === 'date') return dayjs(value as Dayjs).format('YYYY-MM-DD');
  if (kind === 'number') return Number(value);
  return String(value);
};

/**
 * The generations worth showing, newest first.
 *
 * An IO re-signed without its figures changing appends a generation that is
 * identical to the one before it, so a line item can carry several automatic
 * writes that all say the same thing. Listing each is noise: what the log is
 * for is who changed what and why, plus the original write for context.
 *
 * Amends are always kept — one that happened to restore an earlier value is
 * still something somebody did — as is any automatic write that moved a value.
 */
const meaningfulGenerations = (history: BookingGeneration[]): BookingGeneration[] =>
  history
    .filter(
      (generation, index) =>
        index === 0 || Boolean(generation.edited_by) || Boolean(generation.changes?.length)
    )
    .reverse();

/**
 * Amends one booking.
 *
 * One dialog covering every editable field rather than an edit per cell:
 * amends arrive together — a PO and a new end date in the same email — and one
 * reason covering the batch is more honest than repeating it per field.
 */
export const EditBookingModal: FC<Props> = ({ booking, onClose }) => {
  const { t } = useTranslation(NAMESPACE.BOOKING_SHEET_PAGE);
  const [form] = Form.useForm<Values>();
  const [editBooking, { isLoading }] = useEditBookingMutation();
  /* Fetched on demand rather than with the dialog: most amends are made
     without needing to read the previous ones. */
  const [fetchHistory, { data: history, isFetching: isHistoryFetching }] =
    useLazyGetBookingHistoryQuery();

  const rowValue = (name: string) => (booking as unknown as Record<string, unknown>)?.[name];

  /* Prefilled with what is on the row, so an amend reads as a change to known
     values rather than a blank form that might wipe them. */
  useEffect(() => {
    if (!booking) return;
    form.setFieldsValue(
      FIELDS.reduce<Values>((values, field) => {
        values[field.name] = toFormValue(field.kind, rowValue(field.name));
        return values;
      }, {})
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking, form]);

  const close = () => {
    form.resetFields();
    onClose();
  };

  /** A history value, readable: dates and figures as the sheet shows them. */
  const formatValue = (field: string, value: unknown) => {
    if (value === null || value === undefined || value === '') return '—';
    const kind = FIELD_BY_NAME.get(field)?.kind;
    if (kind === 'date') return dayjs(String(value)).format('DD MMM YYYY');
    if (kind === 'number') return Number(value).toLocaleString();
    return String(value);
  };

  const fieldLabel = (field: string) => {
    const spec = FIELD_BY_NAME.get(field);
    return spec ? t(spec.labelKey) : field;
  };

  const renderChange = (change: BookingFieldChange) => (
    <div key={change.field}>
      {fieldLabel(change.field)}: <s>{formatValue(change.field, change.from)}</s>
      {' → '}
      <strong>{formatValue(change.field, change.to)}</strong>
    </div>
  );

  const handleSave = async () => {
    if (!booking) return;
    const values = await form.validateFields();

    /* Only what actually moved is sent. The API rejects a no-op change, and a
       payload of unchanged values would make the amend log claim fields were
       edited when they were not. */
    const changes = FIELDS.reduce<Record<string, Payload>>((acc, field) => {
      const next = toPayload(field.kind, values[field.name]);
      const current = toPayload(field.kind, toFormValue(field.kind, rowValue(field.name)));
      if (next !== current) acc[field.name] = next;
      return acc;
    }, {});

    if (!Object.keys(changes).length) {
      message.info(t('editNoChanges'));
      return;
    }

    try {
      const result = await editBooking({
        lineItemId: booking.nrve_line_item_id,
        reason: (values.reason ?? '').trim(),
        changes
      }).unwrap();
      message.success(t('editSaved', { count: result.changed.length }));
      close();
    } catch (err) {
      /* The API's message names the field it refused and why — an invalid
         date, a flight ending before it starts — which beats generic copy. */
      const detail = (err as { data?: { message?: string } })?.data?.message;
      message.error(detail || t('editFailed'), 8);
    }
  };

  const renderControl = (field: FieldSpec) => {
    if (field.kind === 'date') {
      return <DatePicker format="DD MMM YYYY" style={{ width: '100%' }} />;
    }
    if (field.kind === 'number') {
      return (
        <InputNumber
          style={{ width: '100%' }}
          precision={field.name === 'quantity' ? 0 : undefined}
        />
      );
    }
    return <Input allowClear />;
  };

  return (
    <Modal
      open={Boolean(booking)}
      title={t('editTitle')}
      onCancel={close}
      onOk={handleSave}
      okText={t('editSave')}
      confirmLoading={isLoading}
      /* Wider than the default: two dozen fields stacked in 520px is a long
         scroll for a dialog whose point is seeing what you are changing. */
      width={820}
      destroyOnHidden
    >
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={t('editHint')}
        description={booking?.campaign ?? undefined}
      />

      <Form form={form} layout="vertical" requiredMark={false}>
        {SECTIONS.map((section) => (
          <div key={section.titleKey}>
            <Divider orientation="left" plain style={{ marginTop: 0 }}>
              {t(section.titleKey)}
            </Divider>
            {/* Several columns, so the whole set is visible at once rather
                than scrolled through — an amend is often several fields, and
                comparing them matters. */}
            <Row gutter={16}>
              {section.fields.map((field) => (
                <Col xs={24} sm={12} md={8} key={field.name}>
                  <Form.Item name={field.name} label={t(field.labelKey)}>
                    {renderControl(field)}
                  </Form.Item>
                </Col>
              ))}
            </Row>
          </div>
        ))}

        {/* Collapsed by default and loaded when opened: who changed what, from
            what to what, and why — so an amend can be made with the previous
            ones in view rather than from memory. */}
        <Collapse
          ghost
          items={[
            {
              key: 'history',
              label: t('editHistoryTitle'),
              children: isHistoryFetching ? (
                <span>{t('editHistoryLoading')}</span>
              ) : (
                <Timeline
                  items={meaningfulGenerations(history?.history ?? []).map((generation) => ({
                    key: generation.ingested_at ?? '',
                    color: generation.edited_by ? 'blue' : 'gray',
                    children: (
                      <>
                        <div>
                          <strong>{generation.edited_by ?? t('editHistorySystem')}</strong>
                          {' · '}
                          {generation.edited_at || generation.ingested_at
                            ? dayjs(generation.edited_at ?? generation.ingested_at).format(
                                'DD MMM YYYY, HH:mm'
                              )
                            : '—'}
                        </div>
                        {generation.changes?.map(renderChange)}
                        {generation.edit_reason && <em>{generation.edit_reason}</em>}
                        {!generation.edited_by && !generation.changes?.length && (
                          <em>{t('editHistoryOriginal')}</em>
                        )}
                        {!generation.edited_by && Boolean(generation.changes?.length) && (
                          <em>{t('editHistoryResigned')}</em>
                        )}
                      </>
                    )
                  }))}
                />
              )
            }
          ]}
          onChange={(keys) => {
            if (keys.length && booking) fetchHistory(booking.nrve_line_item_id);
          }}
        />

        <Form.Item
          name="reason"
          label={t('editReason')}
          /* Required because the amend is recorded against whoever made it: a
             figure the team bills from should not change unexplained. */
          rules={[{ required: true, message: t('editReasonRequired') }]}
        >
          <Input.TextArea rows={2} maxLength={300} showCount />
        </Form.Item>
      </Form>
    </Modal>
  );
};
